import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getInvoiceState } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import reorder from "@store/Store/Pages/InvoiceDetails/Handlers/Reorder";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { TYPOGRAPHY } from "@store/theme/design";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// PoolCorp Custom Code
import siteMessage from "@insite/client-framework/SiteMessage";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import { getStoreSettingsCollection } from "@store/Store/Context/ContextSelectors";

const enum fields {
    buttonsOrder = "buttonsOrder",
}

const enum buttons {
    reorder = "Reorder",
}

interface ButtonMapper {
    [key: string]: {
        button: any;
        clickable: any;
        showButtonOnTablet?: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.buttonsOrder]: ButtonModel[];
    };
}

interface ButtonModel {
    fields: {
        name: string;
    };
}

const mapStateToProps = (state: StoreApplicationState) => {
    const order = getInvoiceState(state, state.pages.invoiceDetails.invoiceNumber).value;
    const settingsCollection = getSettingsCollection(state);
    const poolcorpSettings = getStoreSettingsCollection(state).poolcorpSettings;

    return {
        order,
        canReorderItems: poolcorpSettings?.reorderFromInvoiceHistory,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        isReordering: state.store.pages.invoiceDetails.isReordering,
        isUpdatingCart: state.context.isUpdatingCart,
        isVmiOrderDetailsPage: state.pages.orderDetails.isVmiOrderDetailsPage,
        checkoutReviewAndSubmitUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
    };
};

const mapDispatchToProps = {
    reorder,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface InvoiceDetailsReorderButtonStyles {
    buttonsWrapper?: InjectableCss;
    reorderButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
    reorderClickable?: ClickablePresentationProps;
    reorderClickableSpinner?: LoadingSpinnerProps;
    buttonWrapper?: InjectableCss;
    buttonLoadingOverlay?: LoadingOverlayProps;
}

export const invoiceDetailsReorderButtonStyles: InvoiceDetailsReorderButtonStyles = {
    buttonsWrapper: {
        css: css`
            margin-top: ${TYPOGRAPHY.spacing.large};
        `,
    },
    buttonWrapper: {
        css: css`
            margin-top: 0.5em;

            &:first-child {
                margin-top: 0;
            }
        `,
    },
    reorderButton: {
        variant: "primary",
        css: css`
            ${({ theme }) => theme?.button?.primary?.css}
            width: 100%;
        `,
    },
    spinner: {},
    reorderClickableSpinner: {},
    buttonLoadingOverlay: {
        spinnerProps: {
            size: 20,
            css: css`
                position: relative;
                top: -10px;
                left: -10px;
            `,
        },
    },
};

/**
 * @deprecated Use invoiceDetailsReorderButtonStyles instead.
 */
export const emailButtonStyles = invoiceDetailsReorderButtonStyles;
const styles = invoiceDetailsReorderButtonStyles;

const InvoiceDetailsReorderButton: React.FC<Props> = ({
    order,
    reorder,
    fields,
    canReorderItems,
    showAddToCartConfirmationDialog,
    isReordering,
    isUpdatingCart,
    isVmiOrderDetailsPage,
    checkoutReviewAndSubmitUrl,
    history, 
}) => {
    const toasterContext = React.useContext(ToasterContext);
    

    if (!order) {
        return null;
    }

    const hasProductToReorder = !!order.invoiceLines?.find(
        invoiceLine => !!invoiceLine.properties?.productId
    );
    /* PoolCorp Custom Code */
    const checkoutNowClickHandler = () => {
        if (checkoutReviewAndSubmitUrl) {
            history.push(checkoutReviewAndSubmitUrl);
        }
    };
    /* PoolCorp Custom Code */

    const onClickReorder = () => {
        reorder({
            onSuccess: () => {
                if (!showAddToCartConfirmationDialog) {
                    return;
                }
                toasterContext.addToast({
                    body: siteMessage("SelectedInvoice_AddedToCart"),
                    messageType: "success",
                    timeoutLength: 6000,
                    actionButtonConfig: {
                        text: translate("Checkout Now"),
                        onClick: checkoutNowClickHandler,
                        props: {
                            variant: "tertiary",
                            sizeVariant: "small",
                            hoverMode: "lighten"
                        }
                    },
                    cssOverrides: {
                        toast: css`
                            width: 500px;
                        `
                    }
                });
            },
        });
    };

    const buttonList: ButtonMapper = {};
    if (canReorderItems) {
        buttonList[buttons.reorder] = {
            button: (
                <>
                    {/* PoolCorp Custom Code */}
                    <LoadingOverlay {...styles.buttonLoadingOverlay} loading={isReordering}>
                        <Button
                            {...styles.reorderButton}
                            onClick={onClickReorder}
                            disabled={(isUpdatingCart || !hasProductToReorder)}
                            data-test-selector="orderDetails_reorderButton"
                        >
                            {translate("Reorder")}
                        </Button>
                    </LoadingOverlay>
                    {/* PoolCorp Custom Code */}
                </>
            ),
            clickable: (
                <>
                    {!isReordering && (
                        <Clickable {...styles.reorderClickable} onClick={onClickReorder} disabled={isUpdatingCart || !hasProductToReorder}>
                            {translate("Reorder")}
                        </Clickable>
                    )}
                    {isReordering && <LoadingSpinner {...styles.reorderClickableSpinner} />}
                </>
            ),
            showButtonOnTablet: true,
        };
    }


    const buttonsToRender = fields.buttonsOrder.map(button => buttonList[button.fields.name]).filter(button => button);

    return (
        <StyledWrapper {...styles.buttonsWrapper}>
            {buttonsToRender.map((button, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper {...styles.buttonWrapper} key={index}>
                    {button.button}
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(InvoiceDetailsReorderButton)),
    definition: {
        allowedContexts: ["InvoiceDetailsPage"],
        displayName: "Invoice Details Reorder Button",
        group: "Invoice Details",
        fieldDefinitions: [
            {
                name: fields.buttonsOrder,
                displayName: "Buttons Order",
                editorTemplate: "ListField",
                defaultValue: [
                    { fields: { name: buttons.reorder } },
                ],
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                hideAdd: true,
                hideEdit: true,
                hideDelete: true,
                fieldType: "General",
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: "name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
